import { useEffect, useMemo, useState } from "react";
import { useListFilterContext } from "ra-core";
import { GridColumnsMenu } from "@/modules/shared/dataGrid/GridColumnsMenu";
import {
  GridFilterChips,
  GridFilterMenu,
} from "@/modules/shared/dataGrid/GridFilterMenu";
import {
  applyClientFilters,
  isGridFilterKey,
  sortClientRows,
  toServerFilters,
  visibleColumns,
} from "@/modules/shared/dataGrid/gridFilters";
import type { GridSort } from "@/modules/shared/dataGrid/GridTableParts";
import type {
  GridColumn,
  GridFilter,
} from "@/modules/shared/dataGrid/gridTypes";
import { useGridPreferences } from "@/modules/shared/dataGrid/useGridPreferences";

/**
 * Columns + filters for one table, saved per member. Returns the visible
 * columns (member's order) and the toolbar pieces to place on the page.
 * Pass only the columns that apply (e.g. no "Account" when People only).
 */
export const useDataGrid = <Row,>({
  tableId,
  columns,
}: {
  /** Stable, unique per table, e.g. "accounts.people". */
  tableId: string;
  columns: GridColumn<Row>[];
}) => {
  const { prefs, setPrefs, isLoaded } = useGridPreferences(tableId);
  const [editColumnId, setEditColumnId] = useState<string | null>(null);
  // Header menu open on a column (sort + filter from the column itself).
  const [openColumnId, setOpenColumnId] = useState<string | null>(null);
  const filters = useMemo(
    () =>
      (prefs?.filters ?? []).filter((f) =>
        columns.some((c) => c.id === f.columnId && c.filterable),
      ),
    [prefs?.filters, columns],
  );
  const visible = useMemo(
    () => visibleColumns(columns, prefs),
    [columns, prefs],
  );
  const setFilters = (next: GridFilter[]) =>
    setPrefs((current) => ({ ...current, filters: next }));

  const fullFilterButton = (
    <GridFilterMenu
      columns={columns}
      filters={filters}
      onChange={setFilters}
      editColumnId={editColumnId}
      onEditColumnIdChange={setEditColumnId}
    />
  );
  // Filters on visible columns live in their headers; the toolbar button
  // stays only for filters that are not a column (e.g. City, State).
  const filterOnlyColumns = columns.filter((c) => c.filterOnly && c.filterable);
  const visibleIds = new Set(visible.map((c) => c.id));

  return {
    visible,
    filters,
    setFilters,
    isLoaded,
    /** Pass to <GridHeaderRow grid={…} /> (header menus + columns icon). */
    header: {
      header: {
        filters,
        setFilters,
        openColumnId,
        setOpenColumnId,
      },
      columnsIcon: (
        <GridColumnsMenu
          columns={columns}
          prefs={prefs}
          onChange={setPrefs}
          variant="icon"
        />
      ),
    },
    filterButton: filterOnlyColumns.length ? (
      <GridFilterMenu
        columns={filterOnlyColumns}
        filters={filters}
        onChange={setFilters}
        editColumnId={editColumnId}
        onEditColumnIdChange={setEditColumnId}
      />
    ) : null,
    /** Every filter in one menu (mobile lists have no headers). */
    fullFilterButton,
    /** The columns control lives in the table header now. */
    columnsButton: null,
    chips: (
      <GridFilterChips
        columns={columns}
        filters={filters}
        onChange={setFilters}
        onEdit={(id) =>
          visibleIds.has(id) ? setOpenColumnId(id) : setEditColumnId(id)
        }
      />
    ),
  };
};

/**
 * Server lists (ra-core <List>): keeps the grid's filters in the list's
 * filter values, next to the page's own (search, tabs).
 */
export const useGridServerFilters = <Row,>(
  columns: GridColumn<Row>[],
  filters: GridFilter[],
  isLoaded: boolean,
) => {
  const { filterValues, setFilters } = useListFilterContext();
  const wanted = useMemo(
    () => toServerFilters(filters, columns),
    [filters, columns],
  );
  useEffect(() => {
    if (!isLoaded) return;
    const kept = Object.fromEntries(
      Object.entries(filterValues ?? {}).filter(
        ([key]) => !isGridFilterKey(key, columns),
      ),
    );
    const next = { ...kept, ...wanted };
    if (JSON.stringify(next) === JSON.stringify(filterValues ?? {})) return;
    setFilters(next, undefined, false);
    // filterValues is read, not a trigger: only grid changes push filters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanted, isLoaded]);
};

/**
 * Tables with rows already loaded: the grid's filters and a header sort
 * applied in the browser. Pass `sort`/`onSort` to GridHeaderRow.
 */
export const useGridClientRows = <Row,>(
  rows: Row[],
  columns: GridColumn<Row>[],
  filters: GridFilter[],
  initialSort: GridSort | null = null,
) => {
  const [sort, setSort] = useState<GridSort | null>(initialSort);
  const result = useMemo(
    () =>
      sortClientRows(applyClientFilters(rows, filters, columns), sort, columns),
    [rows, filters, columns, sort],
  );
  return { rows: result, sort, onSort: setSort };
};
